"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { useTranslations } from "next-intl";
import { Icon } from "@iconify/react";
import Image from "next/image";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Coloration syntaxique (fond sombre).
const TOKEN_CLASS = {
    kw: "text-fuchsia-400",
    fn: "text-sky-400",
    str: "text-emerald-400",
    num: "text-amber-300",
    var: "text-sky-200",
    op: "text-gray-400",
};

// Chaque pouvoir = une ligne de JS (illustrative) + un comportement réel
// débloqué dans l'aperçu. Ils s'activent dans l'ordre.
const POWERS = [
    {
        id: 1,
        titleKey: "Js_alive_power1_title",
        descKey: "Js_alive_power1_desc",
        tokens: [
            ["coeur", "var"],
            [".", "op"],
            ["addEventListener", "fn"],
            ["(", "op"],
            ['"click"', "str"],
            [", ", "op"],
            ["aimer", "var"],
            [")", "op"],
        ],
    },
    {
        id: 2,
        titleKey: "Js_alive_power2_title",
        descKey: "Js_alive_power2_desc",
        tokens: [
            ["likes", "var"],
            [" = ", "op"],
            ["likes", "var"],
            [" + ", "op"],
            ["1", "num"],
        ],
    },
    {
        id: 3,
        titleKey: "Js_alive_power3_title",
        descKey: "Js_alive_power3_desc",
        tokens: [
            ["if", "kw"],
            [" (", "op"],
            ["likes", "var"],
            [" >= ", "op"],
            ["5", "num"],
            [") ", "op"],
            ["afficherBadge", "fn"],
            ["()", "op"],
        ],
    },
    {
        id: 4,
        titleKey: "Js_alive_power4_title",
        descKey: "Js_alive_power4_desc",
        tokens: [
            ["carte", "var"],
            [".", "op"],
            ["classList", "var"],
            [".", "op"],
            ["toggle", "fn"],
            ["(", "op"],
            ['"sombre"', "str"],
            [")", "op"],
        ],
    },
];

export default function JavascriptComesAlive() {
    const t = useTranslations();
    const reduce = useReducedMotion();

    const [level, setLevel] = useState(0); // nb de pouvoirs activés (0..4)
    const [likes, setLikes] = useState(0);
    const [dark, setDark] = useState(false);
    const [pulse, setPulse] = useState(0);

    const alive = level >= 1;
    const sombre = level >= 4 && dark;
    const populaire = level >= 3 && likes >= 5;

    function tapHeart() {
        if (level < 1) return;
        setPulse((p) => p + 1);
        if (level >= 2) setLikes((l) => Math.min(l + 1, 99));
    }

    function reset() {
        setLevel(0);
        setLikes(0);
        setDark(false);
        setPulse(0);
    }

    // Thème local de la carte (classes explicites, jamais de dark: global).
    const cardSurface = sombre
        ? "bg-slate-900 ring-slate-700"
        : "bg-white ring-slate-200";
    const nameColor = sombre ? "text-white" : "text-slate-900";
    const mutedColor = sombre ? "text-slate-400" : "text-slate-500";
    const textColor = sombre ? "text-slate-300" : "text-slate-700";
    const heartColor = populaire
        ? "text-amber-400"
        : alive
          ? "text-rose-500"
          : sombre
            ? "text-slate-600"
            : "text-slate-300";

    return (
        <section id="javascript-alive" className="pt-16">
            <h2
                className="text-2xl dark:text-white font-bold font-sourceSans"
                style={{ marginBottom: "0.5em" }}
            >
                {t("Js_alive_title")}
            </h2>
            <div className="w-20 h-1 bg-primary rounded mt-3 mb-5"></div>

            <p className="text-black dark:text-lightBlue max-w-[700px] leading-7 font-geistSans mb-0">
                {t("Js_alive_intro")}
            </p>

            <div className="mt-10 relative rounded-3xl overflow-hidden bg-gradient-to-br from-bgDark-700 via-bgDark-800 to-bgDark-900 ring-1 ring-white/10">
                {/* Glows (teinte JS) */}
                <div className="absolute -top-24 -right-24 w-[460px] h-[460px] bg-amber-500/20 rounded-full blur-3xl pointer-events-none" />
                <div className="absolute -bottom-24 -left-20 w-[380px] h-[380px] bg-yellow-500/10 rounded-full blur-3xl pointer-events-none" />

                {/* Barre de fenêtre */}
                <div className="relative flex items-center gap-2 px-5 py-3 border-b border-white/10">
                    <span className="w-3 h-3 rounded-full bg-red-400/90" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400/90" />
                    <span className="w-3 h-3 rounded-full bg-green-400/90" />
                    <span className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-mono text-gray-400">
                        <Icon
                            icon="logos:javascript"
                            className="w-3.5 h-3.5"
                            aria-hidden="true"
                        />
                        post.js
                    </span>
                    <span className="ml-auto inline-flex items-center gap-1.5">
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                        </span>
                        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300">
                            {t("Js_alive_badge")}
                        </span>
                    </span>
                </div>

                <div className="relative p-5 sm:p-7 grid lg:grid-cols-2 gap-6 lg:gap-8">
                    {/* APERÇU VIVANT */}
                    <div className="min-w-0 flex flex-col items-center justify-center gap-3">
                        <div
                            className={`w-full max-w-[340px] rounded-2xl ring-1 p-5 transition-colors duration-300 ${cardSurface}`}
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <Image
                                    src="/images/bebot/bebot-profile.jpg"
                                    width={40}
                                    height={40}
                                    alt={t("Js_alive_card_name")}
                                    className="w-10 h-10 shrink-0 rounded-full object-cover"
                                />
                                <div className="flex-1 min-w-0 leading-tight">
                                    <div
                                        className={`text-sm font-semibold ${nameColor}`}
                                    >
                                        {t("Js_alive_card_name")}
                                    </div>
                                    <div className={`text-xs ${mutedColor}`}>
                                        {t("Js_alive_card_handle")}
                                    </div>
                                </div>
                                {level >= 4 && (
                                    <button
                                        type="button"
                                        onClick={() => setDark((d) => !d)}
                                        aria-label={t("Js_alive_theme")}
                                        className={`shrink-0 flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                                            sombre
                                                ? "bg-white/10 text-amber-300"
                                                : "bg-slate-100 text-slate-500"
                                        }`}
                                    >
                                        <SolarIcon
                                            name={
                                                sombre ? "sun-2-bold" : "moon-bold"
                                            }
                                            className="w-4 h-4"
                                        />
                                    </button>
                                )}
                            </div>

                            <p
                                className={`text-[15px] leading-relaxed mb-4 ${textColor}`}
                            >
                                {t("Js_alive_card_text")}
                            </p>

                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={tapHeart}
                                    disabled={!alive}
                                    aria-label={t("Js_alive_likes")}
                                    className={`inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-sm font-semibold transition active:scale-95 ${
                                        alive
                                            ? sombre
                                                ? "hover:bg-white/10"
                                                : "hover:bg-slate-100"
                                            : "cursor-default opacity-80"
                                    }`}
                                >
                                    <motion.span
                                        key={pulse}
                                        initial={
                                            reduce || pulse === 0
                                                ? false
                                                : { scale: 1.4 }
                                        }
                                        animate={{ scale: 1 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 500,
                                            damping: 14,
                                        }}
                                        className="inline-flex"
                                    >
                                        <SolarIcon
                                            name="heart-angle-bold"
                                            className={`w-[18px] h-[18px] ${heartColor}`}
                                        />
                                    </motion.span>
                                    <span
                                        className={`tabular-nums ${sombre ? "text-slate-200" : "text-slate-700"}`}
                                    >
                                        {likes} {t("Js_alive_likes")}
                                    </span>
                                </button>

                                {populaire && (
                                    <motion.span
                                        initial={
                                            reduce
                                                ? false
                                                : { scale: 0, opacity: 0 }
                                        }
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 360,
                                            damping: 16,
                                        }}
                                        className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 ring-1 ring-amber-400/40 px-2.5 py-1 text-[11px] font-bold text-amber-500"
                                    >
                                        🔥 {t("Js_alive_popular")}
                                    </motion.span>
                                )}
                            </div>
                        </div>

                        {!alive && (
                            <span className="text-xs text-gray-500 font-geistSans">
                                💤 {t("Js_alive_dead")}
                            </span>
                        )}
                    </div>

                    {/* SCRIPT / POUVOIRS */}
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-gray-500 mb-3">
                            <SolarIcon
                                name="code-square-bold"
                                className="w-3.5 h-3.5"
                            />
                            {t("Js_alive_script_label")}
                        </div>

                        <div className="flex flex-col gap-2.5">
                            {POWERS.map((power) => {
                                const unlocked = power.id <= level;
                                const isNext = power.id === level + 1;
                                return (
                                    <div
                                        key={power.id}
                                        className={`rounded-xl ring-1 px-4 py-3 transition-colors duration-300 ${
                                            unlocked
                                                ? "bg-emerald-500/[0.06] ring-emerald-400/25"
                                                : isNext
                                                  ? "bg-white/[0.04] ring-amber-400/30"
                                                  : "bg-white/[0.02] ring-white/10 opacity-50"
                                        }`}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            {unlocked ? (
                                                <SolarIcon
                                                    name="check-circle-bold"
                                                    className="w-5 h-5 text-emerald-400 shrink-0"
                                                />
                                            ) : (
                                                <span
                                                    className={`flex items-center justify-center w-5 h-5 shrink-0 rounded-full text-[11px] font-bold ${
                                                        isNext
                                                            ? "bg-amber-500/20 text-amber-300"
                                                            : "bg-white/10 text-gray-500"
                                                    }`}
                                                >
                                                    {power.id}
                                                </span>
                                            )}
                                            <span
                                                className={`text-sm font-semibold ${
                                                    unlocked
                                                        ? "text-white"
                                                        : isNext
                                                          ? "text-amber-200"
                                                          : "text-gray-500"
                                                }`}
                                            >
                                                {t(power.titleKey)}
                                            </span>
                                        </div>

                                        {unlocked && (
                                            <motion.code
                                                initial={
                                                    reduce
                                                        ? false
                                                        : { opacity: 0, y: -4 }
                                                }
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.25 }}
                                                className="block mt-2 pl-[30px] font-mono text-[12.5px] leading-relaxed whitespace-pre-wrap break-words"
                                            >
                                                {power.tokens.map(
                                                    ([text, type], i) => (
                                                        <span
                                                            key={i}
                                                            className={
                                                                TOKEN_CLASS[type]
                                                            }
                                                        >
                                                            {text}
                                                        </span>
                                                    ),
                                                )}
                                            </motion.code>
                                        )}

                                        {isNext && (
                                            <div className="mt-1.5 pl-[30px]">
                                                <p className="text-[13px] text-gray-400 mb-2.5">
                                                    {t(power.descKey)}
                                                </p>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setLevel(power.id)
                                                    }
                                                    className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 hover:bg-amber-400 px-3.5 py-2 text-[13px] font-semibold text-bgDark-900 transition active:scale-95"
                                                >
                                                    <SolarIcon
                                                        name="bolt-bold"
                                                        className="w-3.5 h-3.5"
                                                    />
                                                    {t("Js_alive_activate")}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {level >= 4 && (
                            <motion.div
                                initial={reduce ? false : { opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.4 }}
                                className="mt-4 flex items-start gap-2.5 rounded-xl bg-emerald-500/10 ring-1 ring-emerald-400/30 px-4 py-3"
                            >
                                <SolarIcon
                                    name="cup-star-bold-duotone"
                                    className="w-5 h-5 text-emerald-300 shrink-0 mt-0.5"
                                />
                                <p className="text-[13px] text-emerald-100 leading-relaxed mb-0">
                                    {t("Js_alive_done")}
                                </p>
                            </motion.div>
                        )}

                        {level > 0 && (
                            <button
                                type="button"
                                onClick={reset}
                                className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.1] ring-1 ring-white/10 px-3.5 py-1.5 text-[13px] text-gray-300 transition active:scale-95"
                            >
                                <SolarIcon
                                    name="restart-bold-duotone"
                                    className="w-3.5 h-3.5"
                                />
                                {t("Js_alive_reset")}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <p className="mt-4 text-sm text-black/60 dark:text-lightBlue/70 max-w-[700px] font-geistSans">
                {t("Js_alive_caption")}
            </p>
        </section>
    );
}
